import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useRecoilState } from "recoil";
import baseKeyState from "../../recoil/baseKeyState";
import capoState from "../../recoil/capoState";
import simpleChordState from "../../recoil/simpleChordState";
import { capoOptions, recommendCapo } from "./easyChords";
import { track } from "../../utils/track";

interface ICapoSheet {
  open: boolean;
  chords: string[];
  onClose: () => void;
}

/** 쉬운 코드로 치기: 카포 위치를 고르면 같은 높이로 더 쉬운 모양의 코드를 보여준다 */
const CapoSheet: React.FC<ICapoSheet> = ({ open, chords, onClose }) => {
  const [baseKey, setBaseKey] = useRecoilState(baseKeyState);
  const [capo, setCapo] = useRecoilState(capoState);
  const [simple, setSimple] = useRecoilState(simpleChordState);
  const toggleSimple = () => {
    if (!simple) track("score:simple");
    try {
      localStorage.setItem("simpleChord", simple ? "0" : "1");
    } catch (e) {}
    setSimple(!simple);
  };
  const base = capo ? capo.base : baseKey;
  const options = useMemo(() => (open ? capoOptions(chords, base, 7) : []), [open, chords, base]);
  const best = useMemo(() => (options.length ? recommendCapo(options) : null), [options]);
  const [picked, setPicked] = useState<number | null>(null);
  if (!open || !best) return null;

  const sel = options[picked ?? (capo ? capo.capo : best.capo)];
  const originalBarre = options[0].chords.filter((c) => c.originalBarre).length;

  const apply = () => {
    track("score:capo_apply");
    if (sel.capo === 0) {
      setBaseKey(base);
      setCapo(null);
    } else {
      setBaseKey((((base - sel.capo) % 12) + 12) % 12);
      setCapo({ capo: sel.capo, base });
    }
    setPicked(null);
    onClose();
  };
  const reset = () => {
    setBaseKey(base);
    setCapo(null);
    setPicked(null);
    onClose();
  };

  return (
    <Dim onClick={() => { setPicked(null); onClose(); }}>
      <Sheet role="dialog" aria-label="쉬운 코드로 치기" onClick={(e) => e.stopPropagation()}>
        <Grip />
        <Title>쉬운 코드로 치기</Title>
        <Desc>카포를 끼우면 원곡과 같은 높이로, 더 쉬운 코드 모양으로 칠 수 있어요</Desc>

        <SimpleRow type="button" role="switch" aria-checked={simple} onClick={toggleSimple}>
          <span>
            <b>코드 단순하게</b>
            <small>Cmaj7·Csus4 → C, Am7 → Am, D/F# → D (초보용)</small>
          </span>
          <Switch on={simple} />
        </SimpleRow>

        <Label>카포 위치</Label>
        <CapoRow>
          {options.map((o) => (
            <CapoButton
              key={o.capo}
              type="button"
              active={o.capo === sel.capo}
              onClick={() => setPicked(o.capo)}
              aria-label={`카포 ${o.capo}`}
            >
              {o.capo}
              {o.capo === best.capo && best.capo > 0 && <Badge>추천</Badge>}
            </CapoButton>
          ))}
        </CapoRow>

        <Table>
          <TableHead>
            <span>원래 코드</span>
            <span />
            <span>{sel.capo === 0 ? "카포 없이" : `카포 ${sel.capo}에서 잡는 코드`}</span>
          </TableHead>
          <TableBody>
            {sel.chords.map((c, i) => (
              <TableRow key={i}>
                <From>
                  <span dangerouslySetInnerHTML={{ __html: c.original }} />
                  {c.originalBarre && <Note>바레</Note>}
                </From>
                <Arrow viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </Arrow>
                <To>
                  <span dangerouslySetInnerHTML={{ __html: c.shape }} />
                  <Note easy={!c.barre}>{c.barre ? "바레" : "쉬움"}</Note>
                </To>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {sel.capo > 0 && (
          <Summary better={sel.barreCount < originalBarre}>
            {sel.barreCount < originalBarre
              ? `어려운 바레 코드가 ${originalBarre}개에서 ${sel.barreCount}개로 줄어요`
              : `바레 코드 ${sel.barreCount}개 (원래 ${originalBarre}개)`}
          </Summary>
        )}

        <Buttons>
          <Ghost type="button" onClick={reset}>원래대로</Ghost>
          <Primary type="button" onClick={apply}>
            {sel.capo === 0 ? "카포 없이 보기" : `카포 ${sel.capo}으로 악보 보기`}
          </Primary>
        </Buttons>
      </Sheet>
    </Dim>
  );
};

export default CapoSheet;

const Dim = styled.div`
  position: fixed;
  inset: 0;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 1200;
  background: rgba(5, 6, 9, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 88vh;
  overflow-y: auto;
  box-sizing: border-box;
  border-radius: 24px 24px 0 0;
  background: #16181e;
  border-top: 1px solid #2a2e38;
  padding: 10px 20px calc(24px + env(safe-area-inset-bottom));
  color: #f3f4f6;
  display: flex;
  flex-direction: column;
`;

const Grip = styled.div`
  align-self: center;
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #3a3f4b;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const Desc = styled.p`
  margin: 6px 0 18px;
  font-size: 14px;
  line-height: 1.5;
  color: #a3a9b5;
  word-break: keep-all;
`;

const SimpleRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 20px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #2a2e38;
  background: #0d0e12;
  color: #f3f4f6;
  text-align: left;
  cursor: pointer;
  & > span {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  & b {
    font-size: 15px;
    font-weight: 600;
  }
  & small {
    font-size: 12px;
    color: #a3a9b5;
    word-break: keep-all;
  }
`;

const Switch = styled.i<{ on: boolean }>`
  flex: none;
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  background: ${({ on }) => (on ? "#ffb547" : "#2a2e38")};
  transition: background 0.2s;
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ on }) => (on ? "21px" : "3px")};
    width: 20px;
    height: 20px;
    border-radius: 10px;
    background: #fff;
    transition: left 0.2s;
  }
`;

const Label = styled.span`
  font-size: 12px;
  color: #7c8392;
  margin-bottom: 14px;
`;

const CapoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 6px;
  margin-bottom: 18px;
`;

const CapoButton = styled.button<{ active: boolean }>`
  position: relative;
  height: 46px;
  border-radius: 12px;
  border: 1px solid ${({ active }) => (active ? "#5b8cff" : "#2a2e38")};
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  color: ${({ active }) => (active ? "#f3f4f6" : "#a3a9b5")};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  outline: 0;
  padding: 0;
`;

const Badge = styled.span`
  position: absolute;
  left: 50%;
  top: -9px;
  transform: translateX(-50%);
  height: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
`;

const Table = styled.div`
  border-radius: 16px;
  background: #0d0e12;
  border: 1px solid #2a2e38;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 28px 1fr;
  padding: 10px 16px;
  font-size: 12px;
  color: #7c8392;
  border-bottom: 1px solid #1e2129;
`;

const TableBody = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 28px 1fr;
  align-items: center;
  height: 42px;
  padding: 0 16px;
  & + & {
    border-top: 1px solid #1e2129;
  }
`;

const From = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 15px;
  color: #a3a9b5;
`;

const To = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #ffb547;
`;

const Note = styled.span<{ easy?: boolean }>`
  font-size: 11px;
  font-weight: 400;
  color: ${({ easy }) => (easy ? "#8fe3b5" : "#7c8392")};
`;

const Arrow = styled.svg`
  width: 16px;
  height: 16px;
  fill: none;
  stroke: #4a505d;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const Summary = styled.div<{ better: boolean }>`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ better }) => (better ? "#13201a" : "#1e2129")};
  color: ${({ better }) => (better ? "#8fe3b5" : "#a3a9b5")};
  font-size: 13px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 18px;
`;

const Ghost = styled.button`
  flex: 1;
  height: 50px;
  border-radius: 14px;
  border: 1px solid #2a2e38;
  background: transparent;
  color: #f3f4f6;
  font-size: 15px;
  cursor: pointer;
  outline: 0;
`;

const Primary = styled.button`
  flex: 2;
  height: 50px;
  border-radius: 14px;
  border: 0;
  background: #5b8cff;
  color: #0d0e12;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  outline: 0;
`;
